/**
 * Dziennik audytu (`aud.*`): hero z kafelkami-filtrami, filtry, oś czasu, panel boczny, analityka, eksport CSV.
 * `audEn` jest typowany względem `audPl` — brakujący klucz to błąd kompilacji.
 */
import { audLabelsEn, audLabelsPl } from './audit-labels';

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const audPl = {
  labels: audLabelsPl,
  title: 'Dziennik audytu',
  subtitle: 'Pełna historia operacji w systemie — kto, co i kiedy zmienił. Wpisy są tylko do odczytu.',
  system: 'System',
  loadError: 'Nie udało się wczytać dziennika audytu.',
  empty: 'Brak wpisów',
  emptyHint: 'Zmień filtry albo poczekaj na aktywność w systemie.',
  noChanges: 'Ten wpis nie zawiera danych przed/po.',
  hero: {
    total: 'Łącznie',
    totalHint: 'Cały rejestr, bez filtrów',
    today: 'Dziś',
    todayHint: 'Wpisy od dzisiejszej północy (cały rejestr)',
    week: '7 dni',
    weekHint: 'Wpisy z dziś i 6 poprzednich dni (cały rejestr)',
    critical: 'Krytyczne',
    criticalHint: 'Wpisy o wadze krytycznej (cały rejestr)',
    security: 'Bezpieczeństwo',
    securityHint: 'Zdarzenia bezpieczeństwa: logowania, 2FA, przełączenia kont (cały rejestr)',
    users: 'Aktywni użytkownicy',
    usersHint: 'Liczba różnych użytkowników z wpisami w rejestrze — to suma, nie filtr',
    actionHint: 'Wpisy: {action} (cały rejestr)',
    daily: 'Ostatnie 14 dni',
    dailyAria: 'Liczba wpisów dzień po dniu w ciągu ostatnich 14 dni',
    filterTiles: 'Skróty filtrów dziennika',
  },
  tabs: { timeline: 'Oś czasu', analytics: 'Analityka', aria: 'Widok dziennika' },
  filters: {
    search: 'Szukaj: encja, ID, adres IP, użytkownik…',
    searchAria: 'Szukaj we wpisach audytu',
    clearSearch: 'Wyczyść wyszukiwanie',
    entity: 'Encja',
    allEntities: 'Wszystkie encje',
    action: 'Akcja',
    allActions: 'Wszystkie akcje',
    user: 'Użytkownik',
    allUsers: 'Wszyscy użytkownicy',
    severity: 'Waga',
    from: 'Od',
    to: 'Do',
    dateRange: 'Zakres dat',
    clearAll: 'Wyczyść wszystko',
    showing: 'Pokazuję {shown} z {total}',
    activeSearch: 'Fraza: „{value}”',
    activeSeverity: 'Waga: {value}',
    activeEntity: 'Encja: {value}',
    activeAction: 'Akcja: {value}',
    activeUser: 'Użytkownik: {value}',
    activeFrom: 'od {value}',
    activeTo: 'do {value}',
    remove: 'Usuń filtr: {value}',
    unknownUser: 'nieznany',
  },
  views: {
    title: 'Widoki',
    save: 'Zapisz widok',
    namePlaceholder: 'Nazwa widoku',
    nameAria: 'Nazwa zapisywanego widoku',
    confirm: 'Zapisz',
    saved: 'Widok zapisany',
    remove: 'Usuń widok: {name}',
    apply: 'Zastosuj widok: {name}',
    hint: 'Zapisane widoki są pamiętane na tym urządzeniu.',
  },
  severity: {
    INFO: 'Informacja',
    WARN: 'Ostrzeżenie',
    CRITICAL: 'Krytyczne',
    SECURITY: 'Bezpieczeństwo',
  },
  actions: {
    CREATE: 'Utworzenie',
    UPDATE: 'Edycja',
    DELETE: 'Usunięcie',
    LOGIN: 'Logowanie',
    LOGIN_FAILED: 'Nieudane logowanie',
    LOGOUT: 'Wylogowanie',
    DEMO_SWITCH: 'Przełączenie konta demo',
    TWO_FACTOR_ENABLED: 'Włączenie 2FA',
    TWO_FACTOR_DISABLED: 'Wyłączenie 2FA',
    EXPORT: 'Eksport danych',
  },
  groups: { today: 'Dziś', yesterday: 'Wczoraj', thisWeek: 'Ten tydzień', older: 'Starsze' },
  list: {
    collapseAll: 'Zwiń wszystkie grupy',
    expandAll: 'Rozwiń wszystkie grupy',
    groupToggle: 'Grupa „{group}”: {count} wpisów',
    changes: '{count} zmian',
    changesOne: '1 zmiana',
    open: 'Otwórz szczegóły wpisu',
    when: 'Czas: {value}',
    retry: 'Spróbuj ponownie',
    ariaRow: '{action}, {entity}, {user}, {time}',
  },
  drawer: {
    title: 'Wpis audytu',
    close: 'Zamknij',
    copyLink: 'Kopiuj link',
    linkCopied: 'Link skopiowany',
    linkFailed: 'Nie udało się skopiować linku.',
    prev: 'Poprzedni wpis',
    next: 'Następny wpis',
    keysHint: 'Strzałki ↑ ↓ — poprzedni / następny wpis, Esc — zamknij',
    user: 'Użytkownik',
    role: 'Rola',
    ip: 'Adres IP',
    time: 'Czas',
    browser: 'Przeglądarka',
    entityId: 'ID obiektu',
    details: 'Szczegóły zdarzenia',
    changesTitle: 'Zmiany ({count})',
    fullData: 'Zapisane dane',
    added: 'dodano',
    removed: 'usunięto',
    changed: 'zmiana',
    unchanged: 'bez zmian',
    showUnchanged: 'Pokaż pola bez zmian ({count})',
    hideUnchanged: 'Ukryj pola bez zmian',
    notFound: 'Nie znaleziono wpisu z linku — mógł zostać usunięty albo jest poza Twoim zakresem.',
    loading: 'Wczytuję wpis…',
  },
  meta: {
    yes: 'tak',
    no: 'nie',
  },
  analytics: {
    severityTitle: 'Rozkład wg wagi',
    severityAria: 'Wykres kołowy rozkładu wpisów według wagi',
    entries: 'wpisów',
    dailyTitle: 'Aktywność — 14 dni',
    dailyAria: 'Wykres słupkowy liczby wpisów w ostatnich 14 dniach, z podziałem na wagę',
    topUsers: 'Najaktywniejsi użytkownicy',
    topActions: 'Najczęstsze akcje',
    topEntities: 'Encje',
    noData: 'Brak danych.',
    loading: 'Ładowanie statystyk…',
    clickHint: 'Kliknij pozycję, aby przefiltrować oś czasu.',
    share: '{count} · {percent}%',
  },
  export: {
    button: 'Eksport CSV',
    busy: 'Eksportuję…',
    done: 'Wyeksportowano {count} wpisów do CSV.',
    truncated: 'Eksport obcięto do {max} wierszy — zawęź filtry, aby pobrać resztę.',
    empty: 'Brak wpisów do eksportu.',
    failed: 'Nie udało się wyeksportować dziennika.',
    header: {
      time: 'Czas',
      severity: 'Waga',
      action: 'Akcja',
      entity: 'Encja',
      entityId: 'ID obiektu',
      user: 'Użytkownik',
      email: 'E-mail',
      role: 'Rola',
      ip: 'Adres IP',
      browser: 'Przeglądarka',
      changes: 'Zmienione pola',
      details: 'Szczegóły',
    },
  },
};

export const audEn: Shape<typeof audPl> = {
  labels: audLabelsEn,
  title: 'Audit log',
  subtitle: 'Complete history of operations — who changed what and when. Entries are read-only.',
  system: 'System',
  loadError: 'Could not load the audit log.',
  empty: 'No entries',
  emptyHint: 'Change the filters or wait for activity in the system.',
  noChanges: 'This entry has no before/after data.',
  hero: {
    total: 'Total',
    totalHint: 'The whole log, no filters',
    today: 'Today',
    todayHint: 'Entries since midnight today (whole log)',
    week: '7 days',
    weekHint: 'Entries from today and the 6 previous days (whole log)',
    critical: 'Critical',
    criticalHint: 'Critical-severity entries (whole log)',
    security: 'Security',
    securityHint: 'Security events: sign-ins, 2FA, account switches (whole log)',
    users: 'Active users',
    usersHint: 'Number of distinct users with entries in the log — a total, not a filter',
    actionHint: 'Entries: {action} (whole log)',
    daily: 'Last 14 days',
    dailyAria: 'Number of entries per day over the last 14 days',
    filterTiles: 'Audit log filter shortcuts',
  },
  tabs: { timeline: 'Timeline', analytics: 'Analytics', aria: 'Audit log view' },
  filters: {
    search: 'Search: entity, ID, IP address, user…',
    searchAria: 'Search audit entries',
    clearSearch: 'Clear search',
    entity: 'Entity',
    allEntities: 'All entities',
    action: 'Action',
    allActions: 'All actions',
    user: 'User',
    allUsers: 'All users',
    severity: 'Severity',
    from: 'From',
    to: 'To',
    dateRange: 'Date range',
    clearAll: 'Clear all',
    showing: 'Showing {shown} of {total}',
    activeSearch: 'Text: “{value}”',
    activeSeverity: 'Severity: {value}',
    activeEntity: 'Entity: {value}',
    activeAction: 'Action: {value}',
    activeUser: 'User: {value}',
    activeFrom: 'from {value}',
    activeTo: 'to {value}',
    remove: 'Remove filter: {value}',
    unknownUser: 'unknown',
  },
  views: {
    title: 'Views',
    save: 'Save view',
    namePlaceholder: 'View name',
    nameAria: 'Name of the view to save',
    confirm: 'Save',
    saved: 'View saved',
    remove: 'Delete view: {name}',
    apply: 'Apply view: {name}',
    hint: 'Saved views are remembered on this device.',
  },
  severity: {
    INFO: 'Info',
    WARN: 'Warning',
    CRITICAL: 'Critical',
    SECURITY: 'Security',
  },
  actions: {
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
    LOGIN: 'Sign-in',
    LOGIN_FAILED: 'Failed sign-in',
    LOGOUT: 'Sign-out',
    DEMO_SWITCH: 'Demo account switch',
    TWO_FACTOR_ENABLED: '2FA enabled',
    TWO_FACTOR_DISABLED: '2FA disabled',
    EXPORT: 'Data export',
  },
  groups: { today: 'Today', yesterday: 'Yesterday', thisWeek: 'This week', older: 'Older' },
  list: {
    collapseAll: 'Collapse all groups',
    expandAll: 'Expand all groups',
    groupToggle: 'Group “{group}”: {count} entries',
    changes: '{count} changes',
    changesOne: '1 change',
    open: 'Open entry details',
    when: 'Time: {value}',
    retry: 'Try again',
    ariaRow: '{action}, {entity}, {user}, {time}',
  },
  drawer: {
    title: 'Audit entry',
    close: 'Close',
    copyLink: 'Copy link',
    linkCopied: 'Link copied',
    linkFailed: 'Could not copy the link.',
    prev: 'Previous entry',
    next: 'Next entry',
    keysHint: 'Arrow keys ↑ ↓ — previous / next entry, Esc — close',
    user: 'User',
    role: 'Role',
    ip: 'IP address',
    time: 'Time',
    browser: 'Browser',
    entityId: 'Object ID',
    details: 'Event details',
    changesTitle: 'Changes ({count})',
    fullData: 'Stored data',
    added: 'added',
    removed: 'removed',
    changed: 'changed',
    unchanged: 'unchanged',
    showUnchanged: 'Show unchanged fields ({count})',
    hideUnchanged: 'Hide unchanged fields',
    notFound: 'The linked entry was not found — it may have been removed or is outside your scope.',
    loading: 'Loading entry…',
  },
  meta: {
    yes: 'yes',
    no: 'no',
  },
  analytics: {
    severityTitle: 'Breakdown by severity',
    severityAria: 'Pie chart of entries by severity',
    entries: 'entries',
    dailyTitle: 'Activity — 14 days',
    dailyAria: 'Bar chart of entries per day over the last 14 days, stacked by severity',
    topUsers: 'Most active users',
    topActions: 'Most frequent actions',
    topEntities: 'Entities',
    noData: 'No data.',
    loading: 'Loading statistics…',
    clickHint: 'Click an item to filter the timeline.',
    share: '{count} · {percent}%',
  },
  export: {
    button: 'Export CSV',
    busy: 'Exporting…',
    done: 'Exported {count} entries to CSV.',
    truncated: 'Export truncated to {max} rows — narrow the filters to get the rest.',
    empty: 'No entries to export.',
    failed: 'Could not export the audit log.',
    header: {
      time: 'Time',
      severity: 'Severity',
      action: 'Action',
      entity: 'Entity',
      entityId: 'Object ID',
      user: 'User',
      email: 'E-mail',
      role: 'Role',
      ip: 'IP address',
      browser: 'Browser',
      changes: 'Changed fields',
      details: 'Details',
    },
  },
};
